'use client';
import React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { usePathname, useSelectedLayoutSegments } from 'next/navigation';

import '@/i18n';

import ProfileProvider from './ProfileProvider';

import BottomBar from '@/components/design/bottomBar';
import { Header } from '@/components/design/header';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import routes from '@/statics/routes';
import { cleanupCookies } from '@/utils/cookieManager';
import createQueryClient from '@/utils/createQueryClient';
import { setDayjsJalaliCalendar } from '@/utils/jalaliDayjs';

type Props = {
  children: React.ReactNode;
};

const Provider: React.FC<Props> = ({ children }) => {
  setDayjsJalaliCalendar();
  const [queryClient] = React.useState(() => createQueryClient({}));

  React.useEffect(() => {
    const cleanCache = async () => {
      const lastCacheClean = localStorage.getItem('cacheCleanDate');
      if (!lastCacheClean) {
        const allLocalStorageItems = { ...localStorage };
        Object.keys(allLocalStorageItems).map((item) =>
          ['lia-token', 'lia_is_webview', 'www.raychat.io'].includes(item)
            ? null
            : localStorage.removeItem(item)
        );
        await cleanupCookies();
        localStorage.setItem('cacheCleanDate', new Date().toISOString());
      }
    };
    void cleanCache();
  }, []);

  const segments = useSelectedLayoutSegments();
  const pathname = usePathname();

  const headerLess = [
    routes.lesson.url.replace('[id]', `${segments[1]}/`),
  ].find((url) => {
    return pathname === url;
  });
  const searchLess = [
    routes.contentBankCategories.url,
    routes.contentBankSubCategory.url,
    routes.contentBankCategory.url.replace('[id]', ''),
    routes.content.url.replace('[id]', ''),
  ].find((url) => pathname.startsWith(url));
  const withBackButton = [
    routes.bookmarks.url,
    routes.content.url.replace('[id]', ''),
  ].find((url) => pathname.startsWith(url));

  return (
    <>
      <div className="fixed -z-10 top-0 left-0 w-dvw h-dvh bg-app-gradient" />
      <QueryClientProvider client={queryClient}>
        <ProfileProvider>
          <div className="w-full h-dvh flex justify-center items-end">
            <ScrollArea
              dir="ltr"
              className="relative w-full max-w-[480px] h-[100dvh] min-[480px]:h-[calc(100dvh-64px)] min-[480px]:rounded-[12px] min-[480px]:shadow-[0_4px_80px_rgba(255,255,255,0.4)] overflow-hidden [&_[data-slot=scroll-area-viewport]]:rounded-none"
            >
              <div
                className="absolute h-dvh w-dvw left-0 top-0 overflow-hidden bg-background -z-10"
                style={{ width: '100%' }}
              >
                <div
                  style={{ zIndex: 1 }}
                  className="absolute rounded-full bd-radial-gradient-1 w-[316px] h-[316px] right-[-100px] top-[10%] sm:w-[250px] sm:h-[250px] sm:right-[-120px] md:w-[316px] md:h-[316px] md:right-[-150px]"
                />
                <div
                  style={{ zIndex: 1 }}
                  className=" absolute rounded-full bd-radial-gradient-1 w-[316px] h-[316px] left-[-100px] bottom-[10%] sm:w-[250px] sm:h-[250px] sm:left-[-120px] md:w-[316px] md:h-[316px] md:left-[-150px]"
                />
              </div>
              <div
                dir="rtl"
                className={cn(
                  'm-auto max-w-[480px] pt-4 pb-24 h-full',
                  Boolean(headerLess) && 'pt-0'
                )}
              >
                {!headerLess && (
                  <Header
                    withBackButton={Boolean(withBackButton)}
                    searchLess={Boolean(searchLess)}
                  />
                )}
                <div className="max-w-[100dvw]">{children}</div>
              </div>
              <BottomBar />
            </ScrollArea>
          </div>
        </ProfileProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
};

export default Provider;
